import React, { useEffect } from "react";
import ConnectionsSection from "./ConnectionsSection";
import {
    ConnectionsMeta,
    ConnectionFilterType,
    NetworkType,
    PriorityType,
    UrgencyType,
    useListConnectionsByFilterQuery,
    Connection,
} from "../../gql/types";
import { ConnectionPlacementType } from "./constants";

interface FilteredConnectionsSectionProps {
    meta: ConnectionsMeta;
    isSelectMode: boolean;
    selectedIds: Set<string>;
    onToggle: (connectionIds: string[]) => void;
    onConnectionsFetched?: (connections: Record<string, Connection>) => void;
    openSection?: boolean;
    placementType?: ConnectionPlacementType;
}

const mapFilterToNetworkType = (filter: ConnectionFilterType): NetworkType => {
    switch (filter) {
        case ConnectionFilterType.School:
            return NetworkType.School;
        case ConnectionFilterType.OldestChild:
            return NetworkType.Neighborhood;
        case ConnectionFilterType.All:
        default:
            return NetworkType.Direct;
    }
};

const getSectionTitle = (filter: ConnectionFilterType): string => {
    switch (filter) {
        case ConnectionFilterType.School:
            return "School Connections";
        case ConnectionFilterType.All:
        default:
            return "My Connections";
    }
};

const FilteredConnectionsSection: React.FC<FilteredConnectionsSectionProps> = ({
    meta,
    isSelectMode,
    selectedIds,
    onToggle,
    onConnectionsFetched,
    openSection = false,
    placementType = ConnectionPlacementType.CONNECTION_HUB,
}) => {
    const { data, error, loading, fetchMore } = useListConnectionsByFilterQuery({
        variables: {
            filter: { type: meta.filter },
            renderType: meta.renderType,
            first: 10,
            after: meta.initialCursor || null,
        },
        fetchPolicy: "cache-and-network",
    });

    const edges = data?.fetchConnectionsByFilter?.edges || [];
    const pageInfo = data?.fetchConnectionsByFilter?.pageInfo;
    const totalCount = data?.fetchConnectionsByFilter?.totalCount || 0;

    const loadMore = async () => {
        if (pageInfo?.hasNextPage) {
            await fetchMore({
                variables: {
                    after: pageInfo.endCursor,
                },
            });
        }
    };

    useEffect(() => {
        if (data?.fetchConnectionsByFilter?.edges && onConnectionsFetched) {
            const connectionsMap: Record<string, Connection> = {};
            for (const edge of data.fetchConnectionsByFilter.edges) {
                const conn = edge.node;
                connectionsMap[conn.connectionID] = conn;
            }
            onConnectionsFetched(connectionsMap);
        }
    }, [data?.fetchConnectionsByFilter?.edges]);

    if (error || edges.length === 0 && (meta.filter !== ConnectionFilterType.All)) {
        return <></>;
    }
    return (
        <ConnectionsSection
            title={getSectionTitle(meta.filter)}
            networkType={mapFilterToNetworkType(meta.filter)}
            priorityType={PriorityType.Low}
            urgencyType={UrgencyType.Low}
            message={null}
            edges={edges}
            count={totalCount}
            loading={loading}
            isPaginated={true}
            pageInfo={pageInfo}
            loadMore={loadMore}
            isSelectMode={isSelectMode}
            selectedIds={selectedIds}
            onToggle={onToggle}
            openSection={openSection}
            placementType={placementType}
        />
    );
};

export default FilteredConnectionsSection;
